import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, MapPin, CreditCard, Users, Scale, Mail, ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Legal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0B0E14]/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl"
          data-testid="legal-modal"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0B0E14]/95 backdrop-blur-xl border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-montserrat font-bold text-2xl text-white">
                {t('legal.title')}
              </h1>
              <p className="text-white/50 text-sm mt-1">Alceylan Enterprises</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="legal-close-btn"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Language Disclaimer - Always visible in English */}
          <div className="mx-6 mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center gap-2 text-amber-400">
              <Globe className="w-5 h-5" />
              <span className="font-semibold text-sm">
                The English version of this document is legally binding. Translations are provided for convenience only.
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Age Restriction */}
            <LegalSection
              icon={Shield}
              title={t('legal.ageRestriction.title')}
              isExpanded={expandedSection === 'age'}
              onToggle={() => toggleSection('age')}
            >
              <p className="text-white/70 mb-4">{t('legal.ageRestriction.intro')}</p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 font-semibold text-sm">
                  {t('legal.ageRestriction.warning')}
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#00E5FF] mt-1">•</span>
                  {t('legal.ageRestriction.point1')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E5FF] mt-1">•</span>
                  {t('legal.ageRestriction.point2')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E5FF] mt-1">•</span>
                  {t('legal.ageRestriction.point3')}
                </li>
              </ul>
            </LegalSection>

            {/* Location & Data Tracking */}
            <LegalSection
              icon={MapPin}
              title={t('legal.locationData.title')}
              isExpanded={expandedSection === 'location'}
              onToggle={() => toggleSection('location')}
            >
              <p className="text-white/70 mb-4">{t('legal.locationData.intro')}</p>
              
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.locationData.geolocationTitle')}</h4>
                  <p className="text-white/60 text-sm">{t('legal.locationData.geolocationDesc')}</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.locationData.videoAudioTitle')}</h4>
                  <p className="text-white/60 text-sm">{t('legal.locationData.videoAudioDesc')}</p>
                  <div className="mt-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-3">
                    <p className="text-[#10B981] text-sm font-medium">{t('legal.locationData.privacyNote')}</p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.locationData.matrixTitle')}</h4>
                  <p className="text-white/60 text-sm">{t('legal.locationData.matrixDesc')}</p>
                </div>
              </div>
            </LegalSection>

            {/* Payments & Refund Policy */}
            <LegalSection
              icon={CreditCard}
              title={t('legal.payments.title')}
              isExpanded={expandedSection === 'payments'}
              onToggle={() => toggleSection('payments')}
            >
              <p className="text-white/70 mb-4">{t('legal.payments.intro')}</p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
                <h4 className="text-yellow-400 font-semibold mb-2">{t('legal.payments.digitalGoods')}</h4>
                <p className="text-white/60 text-sm">{t('legal.payments.digitalGoodsDesc')}</p>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                <h4 className="text-red-400 font-semibold mb-2">{t('legal.payments.noRefund')}</h4>
                <p className="text-white/60 text-sm">{t('legal.payments.noRefundDesc')}</p>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.payments.refundRequest')}</h4>
                <p className="text-white/60 text-sm">{t('legal.payments.refundRequestDesc')}</p>
              </div>
            </LegalSection>

            {/* Safety & Conduct */}
            <LegalSection
              icon={Users}
              title={t('legal.safety.title')}
              isExpanded={expandedSection === 'safety'}
              onToggle={() => toggleSection('safety')}
            >
              <p className="text-white/70 mb-4">{t('legal.safety.intro')}</p>
              
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.safety.messageRequest')}</h4>
                <p className="text-white/60 text-sm">{t('legal.safety.messageRequestDesc')}</p>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h4 className="text-red-400 font-semibold mb-2">{t('legal.safety.zeroTolerance')}</h4>
                <ul className="text-white/60 text-sm space-y-1 mt-2">
                  <li>• {t('legal.safety.harassment')}</li>
                  <li>• {t('legal.safety.hateSpeech')}</li>
                  <li>• {t('legal.safety.illegalContent')}</li>
                  <li>• {t('legal.safety.impersonation')}</li>
                  <li>• {t('legal.safety.spam')}</li>
                </ul>
                <p className="text-red-400 text-sm mt-3 font-medium">{t('legal.safety.consequences')}</p>
              </div>
            </LegalSection>

            {/* Australian Legal Compliance */}
            <LegalSection
              icon={Scale}
              title={t('legal.australian.title')}
              isExpanded={expandedSection === 'australian'}
              onToggle={() => toggleSection('australian')}
            >
              <p className="text-white/70 mb-4">{t('legal.australian.intro')}</p>
              
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.australian.privacyAct')}</h4>
                <p className="text-white/60 text-sm">{t('legal.australian.privacyActDesc')}</p>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.australian.apps')}</h4>
                <p className="text-white/60 text-sm">{t('legal.australian.appsDesc')}</p>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-[#00E5FF] font-semibold mb-2">{t('legal.australian.gdpr')}</h4>
                <p className="text-white/60 text-sm">{t('legal.australian.gdprDesc')}</p>
              </div>
            </LegalSection>

            {/* Contact Us */}
            <LegalSection
              icon={Mail}
              title={t('legal.contact.title')}
              isExpanded={expandedSection === 'contact'}
              onToggle={() => toggleSection('contact')}
            >
              <p className="text-white/70 mb-4">{t('legal.contact.intro')}</p>
              
              <div className="bg-[#008080]/10 border border-[#008080]/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#008080]/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">{t('legal.contact.emailLabel')}</p>
                    <a 
                      href="mailto:info@alceylanenterprises.com" 
                      className="text-[#00E5FF] font-semibold hover:underline"
                    >
                      info@alceylanenterprises.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-white/50 text-sm">
                <p>{t('legal.contact.responseTime')}</p>
              </div>
            </LegalSection>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 px-6 py-4 text-center">
            <p className="text-white/40 text-sm">
              © 2025 Alceylan Enterprises. {t('legal.footer')}
            </p>
            <p className="text-white/30 text-xs mt-1">
              {t('legal.lastUpdated')}: January 2025
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Collapsible Legal Section Component
const LegalSection = ({ icon: Icon, title, children, isExpanded, onToggle }) => (
  <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#008080]/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#00E5FF]" />
        </div>
        <span className="font-montserrat font-semibold text-white">{title}</span>
      </div>
      {isExpanded ? (
        <ChevronUp className="w-5 h-5 text-white/50" />
      ) : (
        <ChevronDown className="w-5 h-5 text-white/50" />
      )}
    </button>
    
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-2">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
